import { isMacAppShell } from './nativeStoreKit.js'

const SIGNUP_REFERRER_KEY = 'signup_referrer'
// API query parameter the captured referrer rides on. Deliberately a query
// param and NOT a custom request header: a custom header is not CORS-safelisted,
// so it only works if the backend echoes it in Access-Control-Allow-Headers —
// and while that entry was missing, EVERY authenticated call from a session that
// had a stored referrer (i.e. all ad traffic) died in the preflight with
// "Failed to fetch". Query params are outside the preflight contract.
// Keep in sync with webknox LinkflareAuthorizationResource.SIGNUP_REFERRER_PARAM.
const SIGNUP_REFERRER_PARAM = 'signup_referrer'
const MAX_REFERRER_LENGTH = 2048
// Per-param cap keeps a hostile/garbled URL from monopolizing the 2048 budget.
const MAX_PARAM_LENGTH = 200
// Full attribution set carried from ad clicks. Order is the canonical
// serialization order. gclid/rdt_cid are stored for a potential future
// offline-conversion upload; nothing reads them yet.
const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'ref',
  'gclid',
  'rdt_cid',
  // Owned-surface attribution: which of our own pages sent this signup
  // (`?lf_from=bookmarks-tool`, `?lf_from=article-<slug>`). Unlike the ad
  // params it arrives with no utm_source alongside it, so it also gets its own
  // capture branch below. Kept last so a serialized string still leads with
  // utm_source whenever an ad click supplied one.
  'lf_from'
]
// Surface markers the app records itself when an auth view finds nothing else
// to store. Everything else in the lf_from vocabulary (`ext-<browser>-install`,
// `mcp-oauth-<client>`, `page:<path>`, …) arrives on the URL from the surface
// that opened the app; webknox PaidSignupStats.classifySurface groups them.
const MAC_APP_MARKER = 'macos-app'
const DIRECT_MARKER = 'direct'

function safeSessionGet(key) {
  try {
    return sessionStorage.getItem(key)
  } catch (e) {
    return null
  }
}

function safeSessionSet(key, value) {
  try {
    sessionStorage.setItem(key, value)
  } catch (e) {
    // sessionStorage may be unavailable in strict privacy contexts.
  }
}

export function normalizeSignupReferrer(value) {
  if (value === null || value === undefined) return null
  const normalized = String(value)
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .trim()
    .slice(0, MAX_REFERRER_LENGTH)
  return normalized || null
}

function normalizeParamValue(value) {
  const normalized = normalizeSignupReferrer(value)
  return normalized ? normalized.slice(0, MAX_PARAM_LENGTH) : null
}

// True when a stored value uses the serialized-attribution format (query
// string) rather than the legacy plain source string.
//
// Matches the FIRST key, not a fixed prefix. `serializeAttribution` emits
// ATTRIBUTION_PARAMS in list order, so the leading key is whichever whitelisted
// param happens to be present — `?gclid=…&lf_from=…` (Google auto-tagging on an
// owned surface, no utm_source anywhere) leads with `gclid=`. A fixed
// utm_source/lf_from prefix list rejected exactly those strings and sent them
// back down the legacy path, losing the whole utm payload.
//
// Still anchored at the start, never `includes`: a legacy external referrer can
// be a URL that merely CONTAINS "utm_source=" (e.g.
// https://blog.example/post?utm_source=newsletter). Its first "key" is the
// whole "https://blog.example/post?utm_source", which is not an attribution
// param, so it stays a legacy string.
function isAttributionString(value) {
  if (typeof value !== 'string' || !value) return false
  const firstKey = value.split('&', 1)[0].split('=', 1)[0]
  return ATTRIBUTION_PARAMS.includes(firstKey)
}

function serializeAttribution(params) {
  const serialized = new URLSearchParams()
  for (const key of ATTRIBUTION_PARAMS) {
    const value = normalizeParamValue(params.get(key))
    if (!value) continue
    // Drop whole params at the cap rather than slicing the ENCODED string:
    // percent-encoding can triple a 200-char value, and a cut landing inside
    // an escape ("…%2") corrupts that param for every decoder downstream.
    const candidate = new URLSearchParams(serialized)
    candidate.set(key, value)
    if (candidate.toString().length > MAX_REFERRER_LENGTH) break
    serialized.set(key, value)
  }
  return serialized.toString()
}

// `includeDocumentReferrer: false` captures ONLY attribution params present in
// the current URL. The app bootstrap uses that mode: it exists to stop the auth
// redirect from dropping ad UTMs, and must not start recording every signed-in
// user's external document.referrer on every app load (that fallback belongs to
// the auth views, SignIn and SignUp). The same goes for the `macos-app` and
// `direct` surface fallbacks: they only run in the default (auth-view) mode.
export function captureSignupReferrer(options = {}) {
  const { includeDocumentReferrer = true } = options
  let params = null
  try {
    params = new URLSearchParams(window.location.search)
  } catch (e) {
    // Non-browser contexts cannot capture.
  }

  // Full attribution (ad traffic): any utm_source in the URL captures the
  // whole whitelisted set as a canonical query string. Last-touch wins.
  if (params && normalizeParamValue(params.get('utm_source'))) {
    const attribution = serializeAttribution(params)
    if (attribution) {
      safeSessionSet(SIGNUP_REFERRER_KEY, attribution)
      return attribution
    }
  }

  // Owned-surface attribution. Our own marketing CTAs carry ONLY `lf_from`
  // (the free tools and in-article CTAs deliberately ship without utm params),
  // so without this branch those signups fall through every case below and get
  // recorded as unattributed. Serializing through the same helper keeps the
  // stored shape identical to the ad path.
  if (params && normalizeParamValue(params.get('lf_from'))) {
    // Never downgrade stored full ad attribution to an lf_from marker. The
    // marketing site's first-touch fallback attaches lf_from to essentially
    // every session now, so an ad-clicked visitor who detours over the
    // marketing site and back would otherwise have their utm set (and the
    // click id needed for offline conversion upload) overwritten by an
    // "organic" landing-page marker. A real later ad click carries utm_source
    // and takes the branch above — last touch between ads still wins.
    const stored = safeSessionGet(SIGNUP_REFERRER_KEY)
    if (isAttributionString(stored) && new URLSearchParams(stored).get('utm_source')) {
      return stored
    }
    const attribution = serializeAttribution(params)
    if (attribution) {
      safeSessionSet(SIGNUP_REFERRER_KEY, attribution)
      return attribution
    }
  }

  // Legacy plain-string referrer (?ref= / ?referrer=), unchanged behavior.
  const queryReferrer = params && (params.get('ref') || params.get('referrer'))
  const normalizedQueryReferrer = normalizeSignupReferrer(queryReferrer)
  if (normalizedQueryReferrer) {
    safeSessionSet(SIGNUP_REFERRER_KEY, normalizedQueryReferrer)
    return normalizedQueryReferrer
  }

  if (!includeDocumentReferrer) {
    return safeSessionGet(SIGNUP_REFERRER_KEY)
  }

  // From here on nothing in the URL named a source. The two surface fallbacks
  // below only ever fill an EMPTY slot: a value an earlier page view stored
  // (an extension's lf_from, an ad click, a referrer) always wins over them.
  const stored = safeSessionGet(SIGNUP_REFERRER_KEY)

  // The macOS app loads the app in a WKWebView with no params and no referrer,
  // so its signups used to land in the unattributed bucket. It announces itself
  // with a flag injected at document start. Checked before the referrer branch:
  // inside the app any "external" referrer is an auth-provider round trip.
  if (!stored && isMacAppShell()) {
    return storeSurfaceMarker(MAC_APP_MARKER)
  }

  let documentReferrer
  try {
    documentReferrer = normalizeSignupReferrer(document.referrer)
  } catch (e) {
    // No document: nothing to record, and nothing proves a direct visit.
    return stored
  }

  let externalReferrer = null
  if (documentReferrer) {
    try {
      const referrerUrl = new URL(documentReferrer, window.location.href)
      if (referrerUrl.origin !== window.location.origin) {
        externalReferrer = documentReferrer
      }
    } catch (e) {
      externalReferrer = documentReferrer
    }
  }

  if (externalReferrer) {
    // Never downgrade a stored full-attribution string to a bare referrer:
    // the ad click's UTM set landed on an earlier page view of this session.
    if (isAttributionString(stored)) {
      return stored
    }
    safeSessionSet(SIGNUP_REFERRER_KEY, externalReferrer)
    return externalReferrer
  }

  // Direct visit: no param, nothing stored, and NO referrer at all (typed URL,
  // bookmark, a link from a desktop mail client, an extension build from before
  // the surface markers). Recording it keeps the unattributed bucket for the
  // cases that deserve a look. A same-origin referrer is not "direct": the
  // visitor's real entry happened on an earlier document we cannot see.
  // It is stored in the attribution format, so the referrer branch above never
  // replaces it (an OAuth provider's referrer after a sign-in round trip would
  // otherwise overwrite it); a later utm or lf_from arrival still does.
  if (!stored && !documentReferrer) {
    return storeSurfaceMarker(DIRECT_MARKER)
  }

  return stored
}

function storeSurfaceMarker(marker) {
  const attribution = serializeAttribution(new URLSearchParams({ lf_from: marker }))
  safeSessionSet(SIGNUP_REFERRER_KEY, attribution)
  return attribution
}

export function peekSignupReferrer() {
  return normalizeSignupReferrer(safeSessionGet(SIGNUP_REFERRER_KEY))
}

// Parsed view of a referrer string for consumers that want structure (e.g.
// Clerk signup metadata). Legacy strings map to { raw, source: raw }.
export function parseSignupAttribution(raw) {
  if (!raw) return null
  if (!isAttributionString(raw)) {
    return { raw, source: raw }
  }
  let parsed
  try {
    parsed = new URLSearchParams(raw)
  } catch (e) {
    return { raw, source: raw }
  }
  return {
    raw,
    source: parsed.get('utm_source') || null,
    medium: parsed.get('utm_medium') || null,
    campaign: parsed.get('utm_campaign') || null,
    term: parsed.get('utm_term') || null,
    content: parsed.get('utm_content') || null,
    ref: parsed.get('ref') || null,
    gclid: parsed.get('gclid') || null,
    rdt_cid: parsed.get('rdt_cid') || null,
    lf_from: parsed.get('lf_from') || null
  }
}

export function peekSignupAttribution() {
  return parseSignupAttribution(peekSignupReferrer())
}

// Builds the Clerk unsafeMetadata payload for a new signup (null when no
// referrer is known). Pure so it is unit-testable without mounting the Clerk
// SignUp view. Legacy `source`/`referrer` fields stay for existing consumers;
// `utm` is added only for full-attribution (ad) traffic.
export function buildSignupMetadata() {
  const referrer = captureSignupReferrer()
  if (!referrer) return null
  const metadata = {
    source: 'referrer',
    referrer
  }
  // Parse what capture just returned rather than re-reading sessionStorage:
  // in private-browsing contexts the write silently fails, and a storage
  // re-read would drop the utm payload for exactly those signups.
  const attribution = parseSignupAttribution(referrer)
  if (attribution && isAttributionString(referrer)) {
    metadata.utm = {
      source: attribution.source,
      medium: attribution.medium,
      campaign: attribution.campaign,
      term: attribution.term,
      content: attribution.content,
      // The channel for organic first-touch signups (referrer hostname).
      // Without it those rows reach Clerk as all-null utm fields while the
      // admin report shows them attributed — two dashboards disagreeing
      // about the same signup.
      ref: attribution.ref,
      gclid: attribution.gclid,
      rdt_cid: attribution.rdt_cid,
      lf_from: attribution.lf_from
    }
  }
  return metadata
}

export { SIGNUP_REFERRER_KEY, SIGNUP_REFERRER_PARAM, MAC_APP_MARKER, DIRECT_MARKER }
